// components/VideoPlayer/ClickableSubtitle/ClickableSubtitle.jsx

import React, { useState } from "react";
import WordInfoModal from "../WordInfoModal/WordInfoModal";
import { useVideo } from "../../../context/VideoContext";
import { useSubtitleSettings } from "../../../context/SubtitleSettingsContext";
import "./ClickableSubtitle.css";

function splitToWords(input, language = "en") {
  if (!input) return { allWords: [], words: [] };

  // پاک کردن تگ‌های HTML
  const cleanText = input.replace(/<[^>]*>/g, "");

  // اگر زبان فارسیه
  if (language === "fa") {
    // برای فارسی، کل متن رو به عنوان یکجا برمیگردونیم بدون جداسازی کلمات
    return {
      allWords: [cleanText], // کل متن به عنوان یک token
      words: [], // هیچ کلمه‌ای برای کلیک نداریم
    };
  }

  // برای زبان‌های غیرفارسی (انگلیسی، آلمانی و ...)
  // بهبود یافته برای کلمات با اپوستروف و خط تیره
  const nonPersianRegex =
    /(\s+|[.,!?;:()\[\]{}<>"''`~@#$%^&*=+\\|/]|\b[a-zA-ZäöüßÄÖÜàâçéèêëïîôûùÀÂÇÉÈÊËÏÎÔÛÙ]+(?:['-][a-zA-ZäöüßÄÖÜàâçéèêëïîôûùÀÂÇÉÈÊËÏÎÔÛÙ]+)*\b)/g;

  const tokens = cleanText.match(nonPersianRegex) || [];

  const allWords = [];
  const words = [];

  for (let token of tokens) {
    if (!token || token === undefined) continue;

    // اگر فاصله یا نشانه‌گذاری بود
    if (
      /^\s+$/.test(token) ||
      /^[.,!?;:()\[\]{}<>"''`~@#$%^&*=+\\|/]$/.test(token)
    ) {
      allWords.push(token);
      continue;
    }

    allWords.push(token);

    // استخراج هسته کلمه (بدون نشانه‌گذاری در ابتدا و انتها)
    const core = token.replace(
      /^[.,!?;:()\[\]{}<>"''`~@#$%^&*=+\\|/\s]+|[.,!?;:()\[\]{}<>"''`~@#$%^&*=+\\|/\s]+$/g,
      "",
    );

    // بررسی می‌کنیم که آیا این یک کلمه معتبر هست (حداقل یک حرف داشته باشه)
    if (/[a-zA-ZäöüßÄÖÜàâçéèêëïîôûùÀÂÇÉÈÊËÏÎÔÛÙ]/.test(core)) {
      words.push(core);
    }
  }

  return { allWords, words };
}

const ClickableSubtitle = ({
  text,
  onWordClick,
  onWordClickComplete,
  className = "",
  subtitleNumber = 1, // مشخص میکنه کدوم زیرنویس (1 یا 2)
}) => {
  const [selectedWord, setSelectedWord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { videoMetadata, subtitle1Text, subtitle2Text } = useVideo();
  const { settings } = useSubtitleSettings();

  // دریافت زبان زیرنویس از context بر اساس شماره زیرنویس
  const language =
    subtitleNumber === 1
      ? settings.subtitle1.language
      : settings.subtitle2.language;

  // اگر زبان فارسیه، اصلاً کلمات رو جدا نکن و همه رو غیرقابل کلیک نشون بده
  if (language === "fa") {
    const cleanText = text.replace(/<[^>]*>/g, "");

    return (
      <div className={`clickable-subtitle ${className}`}>
        <div className="subtitle-text">{cleanText}</div>
      </div>
    );
  }

  const { allWords } = splitToWords(text || "", language);

  const handleWordClick = (e, word) => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedWord(word);
    setIsModalOpen(true);

    if (onWordClick) onWordClick(word);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedWord(null);

    if (onWordClickComplete) {
      onWordClickComplete();
    }
  };

  const handleSave = () => {
    handleCloseModal();
  };

  const renderText = () => {
    return allWords.map((token, index) => {
      // اگر token خالی باشه
      if (!token) return null;

      // اگر فاصله یا نشانه‌گذاری هست
      if (
        /^\s+$/.test(token) ||
        /^[.,!?;:()\[\]{}<>"''`~@#$%^&*=+\\|/]$/.test(token)
      ) {
        return (
          <span key={index} className="non-word">
            {token}
          </span>
        );
      }

      // استخراج هسته کلمه برای بررسی
      const core = token.replace(
        /^[.,!?;:()\[\]{}<>"''`~@#$%^&*=+\\|/\s]+|[.,!?;:()\[\]{}<>"''`~@#$%^&*=+\\|/\s]+$/g,
        "",
      );

      // اگر حداقل یک حرف لاتین داشته باشه
      if (/[a-zA-ZäöüßÄÖÜàâçéèêëïîôûùÀÂÇÉÈÊËÏÎÔÛÙ]/.test(core)) {
        return (
          <span
            key={index}
            className="clickable-word"
            onClick={(e) => handleWordClick(e, token)}
          >
            {token}
          </span>
        );
      }

      // اگر هیچ حرف لاتینی نداره
      return (
        <span key={index} className="non-word">
          {token}
        </span>
      );
    });
  };

  return (
    <>
      <div
        className={`clickable-subtitle ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="subtitle-text">{renderText()}</div>
      </div>

      <WordInfoModal
        isOpen={isModalOpen}
        word={selectedWord}
        fullText={text}
        onClose={handleCloseModal}
        onSave={handleSave}
        fromLang={language}
        toLang="fa"
        movieName={videoMetadata?.filmName || ""}
        season={videoMetadata?.season || ""}
        episode={videoMetadata?.episode || ""}
        subtitle1Text={subtitle1Text}
        subtitle2Text={subtitle2Text}
      />
    </>
  );
};

export default ClickableSubtitle;
